import { clientApi, refreshClient } from "@/utils/api";
import { setToken } from "@/utils/token";
import { refreshCenterOAuthToken } from "@/features/auth/services/centerOAuthApi";

import Cookies from "js-cookie";
import { TGetMeResDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useCallback } from "react";
import { TUserPermission } from "@/types/enum";
import { TRefreshTokenReqDto, TRefreshTokenResDto } from "@/api/services/main/auth/dtos/refresh-token.dto";
import { TSendEmailToChangeUserEmailReqDto, TSendEmailToChangeUserEmailResDto } from "@/api/services/main/auth/dtos/send-email-to-change-email.dto";
import { TUpdateUserEmailReqDto, TUpdateUserEmailResDto } from "@/api/services/main/auth/dtos/update-user-email.dto";
import { TLogoutResDto } from "@/api/services/main/auth/dtos/logout.dto";
import { TValidateNewEmailReqDto, TValidateNewEmailResDto } from "@/api/services/main/auth/dtos/validate-new-email-exists.dto";

export const fetchMeApi = async (): Promise<TGetMeResDto> => {
  const { data } = await clientApi.get<TGetMeResDto>("/get-me");
  return data;
};

export const useMe = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER.FETCH_ME],
    queryFn: fetchMeApi,
  });
};

export const usePermissions = () => {
  const { data, isLoading } = useMe();
  const user = data?.data;
  const permissions = user?.permissions;
  const hasPermission = useCallback(
    (_code: TUserPermission) => {
      if (isLoading || !user) return false;
      // Subtype gating removed (admin/user collapse): every non-admin user has full permissions.
      return true;
    },
    [isLoading, user?.type, user?.permissions],
  );
  return { permissions, hasPermission, isLoading };
};

// Shared refresh promise to prevent concurrent refresh calls (race condition between interceptor, withAuth, SocketProvider)
let refreshPromise: Promise<TRefreshTokenResDto> | null = null;

// Use refreshClient (no auth header, no interceptors) to avoid sending expired token
export const refreshTokenApi = async (payload: TRefreshTokenReqDto): Promise<TRefreshTokenResDto> => {
  // Reuse in-flight refresh request to avoid race conditions
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      // Center handoff (SSO) sessions hold center-pool tokens → must refresh at center.
      // Native /login sessions hold account-pool tokens → refresh at account.
      if (Cookies.get("x-auth-source") === "center") {
        const accessToken = Cookies.get("match_token") || "";
        const data = await refreshCenterOAuthToken(accessToken, payload.refreshToken);
        const refreshToken = data.refreshToken || payload.refreshToken;
        setToken(data.accessToken, refreshToken);
        return { data: { accessToken: data.accessToken, expiresIn: data.expiresIn, refreshToken } };
      }

      const rs = await refreshClient.post<TRefreshTokenResDto>("/refresh-token", payload);
      const { data } = rs.data;
      // Cognito REFRESH_TOKEN_AUTH may not return a new refreshToken — keep the existing one
      setToken(data.accessToken, data.refreshToken || payload.refreshToken);
      return rs.data;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const sendEmailToChangeEmailApi = async (payload: TSendEmailToChangeUserEmailReqDto): Promise<TSendEmailToChangeUserEmailResDto> => {
  const { data } = await clientApi.post<TSendEmailToChangeUserEmailResDto>("/send-email-to-change-user-email", payload);
  return data;
};

export const updateUserEmailApi = async (payload: TUpdateUserEmailReqDto): Promise<TUpdateUserEmailResDto> => {
  const { data } = await clientApi.post<TUpdateUserEmailResDto>("/update-user-email", payload);
  return data;
};

export const logoutApi = async (): Promise<TLogoutResDto> => {
  const rs = await clientApi.post<TLogoutResDto>("/logout");
  return rs?.data;
};

export const validateNewEmailExistsApi = async (payload: TValidateNewEmailReqDto): Promise<TValidateNewEmailResDto> => {
  const { data } = await clientApi.post<TValidateNewEmailResDto>("/validate-new-email-exists", payload);
  return data;
};
